import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Box,
  Grid,
  Select,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  FaPlus,
  FaUpload,
  FaCopy,
  FaTags,
  FaCalendarAlt,
  FaBarcode,
  FaBuilding,
  FaUserEdit,
  FaBook,
} from "react-icons/fa";
import { BASE_URL } from "../../../../Globalapi/Globalapi";

const AddBook = () => {
  const [open, setOpen] = useState(false); // State to control modal visibility
  const [bookDetails, setBookDetails] = useState({
    title: "",
    author: "",
    publisher: "",
    isbn: "",
    publication_year: "",
    total_copies: "",
    image: "",
    category_id: "", // Category ID for the API
  });
  const [categories, setCategories] = useState([]); // State for categories

  const schoolId = sessionStorage.getItem("school_id");
  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/admin/library/category/list_category.php?school_id=${schoolId}`
        );
        const data = await response.json();

        if (data.success) {
          setCategories(data.data);
        } else {
          console.error("Failed to fetch categories: ", data.message);
        }
      } catch (error) {
        console.error("Error fetching categories: ", error);
      }
    };

    fetchCategories();
  }, []);

  // Function to handle modal opening
  const handleOpen = () => setOpen(true);

  // Function to handle modal closing
  const handleClose = () => setOpen(false);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setBookDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  // Handle image upload
  const handleImageChange = (e) => {
    setBookDetails({
      ...bookDetails,
      image: URL.createObjectURL(e.target.files[0]), // Temporarily store image URL
    });
  };

  // Submit form (add book)
  const handleSubmit = async () => {
    const data = {
      user_id: sessionStorage.getItem("user_id"), // Replace with actual user ID from session or context
      school_id: sessionStorage.getItem("school_id"), // Replace with dynamic school ID if needed
      category_id: bookDetails.category_id,
      title: bookDetails.title,
      author: bookDetails.author,
      isbn: bookDetails.isbn,
      publisher: bookDetails.publisher,
      publication_year: bookDetails.publication_year,
      total_copies: bookDetails.total_copies,
    };
    console.log("data", data);

    try {
      const response = await fetch(
        `${BASE_URL}/admin/library/books/create_book.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );
      console.log("book create", response);

      const result = await response.json();

      if (result.success) {
        console.log("Book created successfully:", result.message);
        handleClose(); // Close modal after submission
      } else {
        console.error("Error creating book:", result.message);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // Responsive grid layout: one column for small screens, two columns for larger screens
  const gridTemplateColumns = useBreakpointValue({
    base: "1fr", // Single column on small screens
    md: "1fr 1fr", // Two columns on medium and larger screens
  });

  return (
    <div>
      <Button leftIcon={<FaPlus />} colorScheme="teal" onClick={handleOpen}>
        Add Book
      </Button>

      <Modal
        isOpen={open}
        onClose={handleClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Add Book
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {/* Responsive Grid Layout */}
            <Grid templateColumns={gridTemplateColumns} gap={4} mb={4}>
              <FormControl id="title">
                <FormLabel>
                  {" "}
                  <Icon as={FaBook} mr={1} color="teal.500" />
                  Book Title
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="text"
                  name="title"
                  value={bookDetails.title}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="author">
                <FormLabel>
                  {" "}
                  <Icon as={FaUserEdit} mr={1} color="teal.500" />
                  Author
                </FormLabel>
                <Input
                  type="text"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  name="author"
                  value={bookDetails.author}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="publisher">
                <FormLabel>
                  {" "}
                  <Icon as={FaBuilding} mr={1} color="teal.500" />
                  Publisher
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="text"
                  name="publisher"
                  value={bookDetails.publisher}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="isbn">
                <FormLabel>
                  {" "}
                  <Icon as={FaBarcode} mr={1} color="teal.500" />
                  ISBN
                </FormLabel>
                <Input
                  type="text"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  name="isbn"
                  value={bookDetails.isbn}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="publication_year">
                <FormLabel>
                  {" "}
                  <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                  Publication Year
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="text"
                  name="publication_year"
                  value={bookDetails.publication_year}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="category_id">
                <FormLabel>
                  {" "}
                  <Icon as={FaTags} mr={1} color="teal.500" />
                  Category
                </FormLabel>
                <Select
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  name="category_id"
                  value={bookDetails.category_id}
                  onChange={handleChange}
                  placeholder="Select Category"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl id="total_copies">
                <FormLabel>
                  {" "}
                  <Icon as={FaCopy} mr={1} color="teal.500" />
                  Total Copies
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="number"
                  name="total_copies"
                  value={bookDetails.total_copies}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl id="image">
                <FormLabel>
                  {" "}
                  <Icon as={FaUpload} mr={1} color="teal.500" />
                  Upload Book Image
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 1px teal.500",
                  }}
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                />
                {bookDetails.image && (
                  <Box mt={2}>
                    <img
                      src={bookDetails.image}
                      alt="Book Cover"
                      style={{ width: "100px", height: "auto" }}
                    />
                  </Box>
                )}
              </FormControl>
            </Grid>
          </ModalBody>

          <ModalFooter>
            {/* <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button> */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSubmit}
            >
              Add Book
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default AddBook;
